import React, { useState, useEffect } from 'react';
import { websiteAnalyticsService } from '../../services/websiteAnalyticsService';
import { ConversionFunnel, FunnelStep, DropOffPoint, WebsiteAnalyticsFilters } from '../../types/websiteAnalytics';
import BarChart from '../Charts/BarChart';
import MetricCard from '../Common/MetricCard';

interface ConversionFunnelVisualizationProps {
  filters?: WebsiteAnalyticsFilters;
  refreshInterval?: number;
}

export const ConversionFunnelVisualization: React.FC<ConversionFunnelVisualizationProps> = ({
  filters,
  refreshInterval = 60000 // Changed to 1 minute for more responsive updates
}) => {
  const [funnels, setFunnels] = useState<ConversionFunnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFunnel, setSelectedFunnel] = useState<string>('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchConversionFunnels = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getConversionFunnels(filters);
      
      if (response.success && response.data) {
        // Handle both array and wrapped response
        const funnelsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
        setFunnels(funnelsData);
        if (funnelsData.length > 0 && !selectedFunnel) {
          setSelectedFunnel(funnelsData[0].name);
        }
        setError(null);
        setLastRefresh(new Date());
      } else {
        setError('Failed to fetch conversion funnel data');
        setFunnels([]);
      }
    } catch (err) {
      setError('Error loading conversion funnel data');
      console.error('Error fetching conversion funnels:', err);
      setFunnels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    fetchConversionFunnels();
  };

  useEffect(() => {
    fetchConversionFunnels();
    
    const interval = setInterval(fetchConversionFunnels, refreshInterval);
    return () => clearInterval(interval);
  }, [filters, refreshInterval]);

  if (loading && funnels.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchConversionFunnels}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (funnels.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No conversion funnel data available</p>
      </div>
    );
  }

  const currentFunnel = funnels.find(funnel => funnel.name === selectedFunnel) || funnels[0];

  const formatPercentage = (value: number): string => {
    return websiteAnalyticsService.formatPercentage(value);
  };

  const formatNumber = (value: number): string => {
    return websiteAnalyticsService.formatNumber(value);
  };

  // Calculate funnel metrics
  const calculateFunnelMetrics = (funnel: ConversionFunnel) => {
    const steps = funnel?.steps || [];
    const dropOffPoints = funnel?.dropOffPoints || [];
    
    const totalDropOffs = steps.reduce((sum, step) => sum + ((step.sessions || 0) * (step.dropOffRate || 0)), 0);
    const averageConversionRate = steps.length > 0 
      ? steps.reduce((sum, step) => sum + (step.conversionRate || 0), 0) / steps.length 
      : 0;
    const biggestDropOff = dropOffPoints.length > 0 
      ? dropOffPoints.reduce((max, dropOff) => 
      (dropOff.dropOffRate || 0) > (max.dropOffRate || 0) ? dropOff : max
    )
      : { fromStep: '', toStep: '', dropOffRate: 0, usersLost: 0 };

    return {
      totalDropOffs,
      averageConversionRate,
      biggestDropOff
    };
  };

  const metrics = calculateFunnelMetrics(currentFunnel);

  // Prepare funnel chart data for Recharts
  const currentSteps = currentFunnel?.steps || [];
  const funnelChartData = currentSteps.map(step => ({
    name: step.name || 'Unknown',
    sessions: step.sessions || 0
  }));

  const conversionRateChartData = currentSteps.map(step => ({
    name: step.name || 'Unknown',
    conversionRate: parseFloat(((step.conversionRate || 0) * 100).toFixed(1))
  }));

  const dropOffChartData = currentSteps.slice(1).map(step => ({
    name: step.name || 'Unknown',
    dropOffRate: parseFloat(((step.dropOffRate || 0) * 100).toFixed(1))
  }));

  const getStepIcon = (stepName: string): string => {
    const name = stepName.toLowerCase();
    if (name.includes('visit') || name.includes('landing')) return '🏠';
    if (name.includes('browse') || name.includes('search')) return '🔍';
    if (name.includes('view') || name.includes('product')) return '👁️';
    if (name.includes('cart') || name.includes('add')) return '🛒';
    if (name.includes('checkout') || name.includes('payment')) return '💳';
    if (name.includes('complete') || name.includes('success')) return '✅';
    return '📊';
  };

  const getDropOffColor = (rate: number): string => {
    if (rate > 0.7) return 'text-red-600 bg-red-100';
    if (rate > 0.4) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Funnel Selector - Responsive */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900">Conversion Funnel Analysis</h3>
            <p className="text-xs sm:text-sm text-gray-600">Track user journey from visit to conversion</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleManualRefresh}
              disabled={loading}
              className="px-3 py-2 text-xs sm:text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
              title={`Last updated: ${lastRefresh.toLocaleTimeString()}`}
            >
              <span className={loading ? 'animate-spin' : ''}>🔄</span>
              <span className="hidden xs:inline">{loading ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            <select
              id="funnel-selector"
              value={selectedFunnel}
              onChange={(e) => setSelectedFunnel(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {funnels.map(funnel => (
                <option key={funnel.name} value={funnel.name}>
                  {funnel.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Metrics - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Total Sessions"
          value={formatNumber(currentFunnel.totalSessions)}
          icon={<span className="text-lg sm:text-xl">👥</span>}
          color="blue"
        />
        <MetricCard
          title="Completion Rate"
          value={formatPercentage(currentFunnel.completionRate)}
          icon={<span className="text-lg sm:text-xl">✅</span>}
          color={currentFunnel.completionRate > 0.1 ? 'green' : 'red'}
        />
        <MetricCard
          title="Avg Conversion"
          value={formatPercentage(metrics.averageConversionRate)}
          icon={<span className="text-lg sm:text-xl">📈</span>}
          color={metrics.averageConversionRate > 0.2 ? 'green' : 'red'}
        />
        <MetricCard
          title="Total Drop-offs"
          value={formatNumber(metrics.totalDropOffs)}
          icon={<span className="text-lg sm:text-xl">📉</span>}
          color="red"
        />
      </div>

      {/* Funnel Visualization - Responsive */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold mb-4 sm:mb-6">Funnel Steps</h3>
        
        <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
          {currentSteps.map((step, index) => {
            const width = ((step.sessions || 0) / (currentFunnel.totalSessions || 1)) * 100;
            const isLast = index === currentSteps.length - 1;
            
            return (
              <div key={step.name} className="relative">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-medium">
                    {index + 1}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="text-sm sm:text-lg">{getStepIcon(step.name)}</span>
                        <span className="font-medium text-xs sm:text-sm text-gray-900 truncate">{step.name}</span>
                      </div>
                      <div className="text-[10px] sm:text-sm text-gray-600">
                        {formatNumber(step.sessions)} ({formatPercentage(step.sessions / currentFunnel.totalSessions)})
                      </div>
                    </div>
                    
                    <div className="relative">
                      <div className="w-full bg-gray-200 rounded-full h-5 sm:h-8">
                        <div
                          className="bg-blue-600 h-5 sm:h-8 rounded-full flex items-center justify-end pr-2 sm:pr-3 text-white text-[10px] sm:text-sm font-medium"
                          style={{ width: `${Math.max(width, 15)}%` }}
                        >
                          {formatPercentage(step.conversionRate)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {!isLast && (
                  <div className="flex items-center justify-center mt-1 sm:mt-2 mb-1 sm:mb-2">
                    <div className="flex items-center gap-1 sm:gap-2 text-red-600">
                      <span className="text-xs sm:text-sm">↓</span>
                      <span className="text-[10px] sm:text-sm font-medium">
                        {formatPercentage(step.dropOffRate)} drop-off
                      </span>
                      <span className="text-xs sm:text-sm">↓</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts - Stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">Sessions by Step</h3>
          <div className="h-48 sm:h-64">
            <BarChart
              data={funnelChartData}
              xAxisKey="name"
              bars={[{ key: 'sessions', color: '#3B82F6', name: 'Sessions' }]}
              height={192}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">Conversion Rates</h3>
          <div className="h-48 sm:h-64">
            <BarChart
              data={conversionRateChartData}
              xAxisKey="name"
              bars={[{ key: 'conversionRate', color: '#10B981', name: 'Conversion Rate (%)' }]}
              height={192}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">Drop-off Rates</h3>
          <div className="h-48 sm:h-64">
            <BarChart
              data={dropOffChartData}
              xAxisKey="name"
              bars={[{ key: 'dropOffRate', color: '#EF4444', name: 'Drop-off Rate (%)' }]}
              height={192}
            />
          </div>
        </div>
      </div>

      {/* Drop-off Analysis - Responsive Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm sm:text-lg font-semibold text-gray-900">Drop-off Analysis</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">From</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">To</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Count</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Rate</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(currentFunnel?.dropOffPoints || [])
                .sort((a, b) => (b.dropOffRate || 0) - (a.dropOffRate || 0))
                .slice(0, 5)
                .map((dropOff) => (
                  <tr key={`${dropOff.fromStep}-${dropOff.toStep}`} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[80px] sm:max-w-none">{dropOff.fromStep}</td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-600 truncate max-w-[80px] sm:max-w-none">{dropOff.toStep}</td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 hidden sm:table-cell">{formatNumber(dropOff.dropOffCount)}</td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4">
                      <span className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${getDropOffColor(dropOff.dropOffRate)}`}>
                        {formatPercentage(dropOff.dropOffRate)}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Optimization Insights - Responsive */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
        <h3 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">Optimization Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-xs sm:text-sm">🎯 Key Opportunities</h4>
            <div className="space-y-2 sm:space-y-3">
              <div className="p-2 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-[10px] sm:text-sm font-medium text-red-800">Biggest Drop-off</div>
                <div className="text-[10px] sm:text-sm text-red-600">{metrics.biggestDropOff.fromStep} → {metrics.biggestDropOff.toStep}</div>
                <div className="text-[10px] sm:text-sm text-red-600">{formatPercentage(metrics.biggestDropOff.dropOffRate)} drop-off rate</div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-xs sm:text-sm">📈 Performance Summary</h4>
            <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Overall Completion:</span>
                <span className="font-medium">{formatPercentage(currentFunnel.completionRate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Users Lost:</span>
                <span className="font-medium">{formatNumber((currentFunnel.totalSessions || 0) - (currentSteps[currentSteps.length - 1]?.sessions || 0))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Potential:</span>
                <span className="font-medium text-green-600">+{formatNumber(Math.round(currentFunnel.totalSessions * 0.1))} conversions</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};